/**
 * @ngdoc service
 * @name ims.map.service:EntityVisualiser
 * @description
 * Entity Visualiser class
 * Returns the values required by Cesium to display an entity on the map
 */

import { ILabelGetter } from 'app/views/value_getters/ILabelGetter';
import Constant from '../../shared/utilities/constant';
import {
    SHOW_ASSET_NONE,
    SHOW_LABEL_ALWAYS,
    SHOW_LABEL_IMMOBILE,
    SHOW_LABEL_NONE,
} from '../base_view_constants';
import { MOBILITY_IMMOBILE, MOBILITY_SEMI_MOBILE } from 'app/shared/collections/asset_type';
import { MapOptionsService } from 'app/shared/map_options/MapOptionsService';
import { IMSAggregator } from '../aggregators/aggregator';
import { IMSValueGetter } from '../value_getters/value_getter';
import { BaseColorRenderer } from 'app/shared/utilities/color_renderer';
import { ImageUrlService } from 'app/shared/image_url.service';
import { isNullOrUndefined } from 'app/shared/utils';
import { Zone } from 'app/shared/collections/zones/zone';
import {
    ASSET_DISPLAY_AGGREGATOR,
    ASSET_DISPLAY_COLOUR,
    ASSET_LABEL_DISPLAY_TYPE,
    ASSET_LABEL_DISPLAY_VISIBILITY,
    LABEL_TYPE_OPTION_NAME,
    ZONE_DISPLAY_AGGREGATOR,
    ZONE_LABEL_DISPLAY_TYPE,
    ZONE_LABEL_DISPLAY_VISIBILITY,
} from '../BaseViewController';
import { Asset } from 'app/shared/collections/asset';
import { ImsObjectVisibilityService } from 'app/shared/visibility/ImsObjectVisibilityService';
import { ImsItemTypeRegistry } from '../../shared/ims_items/ImsItemTypeRegistry';

export class EntityVisualiser {
    protected _colorRenderer: BaseColorRenderer;
    protected _valueGetter: IMSValueGetter;
    protected _labelValueGetter: ILabelGetter;
    protected _imageUrlService: ImageUrlService;
    protected _assetOptionsService: MapOptionsService;
    protected _zoneOptionsService: MapOptionsService;
    protected _aggregators: IMSAggregator[];
    protected _aggregatorsMap: Map<string, IMSAggregator>;
    protected _imsObjectVisibilityService: ImsObjectVisibilityService;

    protected listeners = [];
    private _imsItemTypeRegistry: ImsItemTypeRegistry;

    constructor(
        colorRenderer: BaseColorRenderer,
        valueGetter: IMSValueGetter,
        imageUrlService: ImageUrlService,
        assetOptionsService: MapOptionsService,
        zoneOptionsService: MapOptionsService,
        imsObjectVisibilityService: ImsObjectVisibilityService,
        imsItemTypeRegistry: ImsItemTypeRegistry,
        aggregators: IMSAggregator[],
        labelValueGetter: ILabelGetter = valueGetter,
    ) {
        this._colorRenderer = colorRenderer;
        this._valueGetter = valueGetter;
        this._imageUrlService = imageUrlService;
        this._assetOptionsService = assetOptionsService;
        this._zoneOptionsService = zoneOptionsService;
        this._imsObjectVisibilityService = imsObjectVisibilityService;
        this._imsItemTypeRegistry = imsItemTypeRegistry;
        this._labelValueGetter = labelValueGetter;

        /**
         * @ngdoc property
         * @name aggregator
         * @propertyOf ims.map.service:IMSVisualiser
         * @description
         * The IMSAggregator instances for this entity visualiser. These are used to combine multiple stats
         * on one asset together, such as in the case of IPDeviceStats where multiple IP Devices can have a stat
         * at the same time, or where Asset-parent stats have a subgroup defined.
         */
        this._aggregators = aggregators;

        // Map aggregators to their display names
        this._aggregatorsMap = this._aggregators.reduce((accumulator, value) => {
            accumulator.set(value.displayName, value);
            return accumulator;
        }, new Map());
    }

    /**
     * Gets the currently selected Asset imsAggregator based on map options
     */
    getSelectedAssetAggregator() {
        return (
            this._aggregatorsMap.get(
                this._assetOptionsService.selectedMapOptions[ASSET_DISPLAY_AGGREGATOR],
            ) || this._aggregators[0]
        );
    }

    /**
     * Gets the currently selected Zone imsAggregator based on map options
     */
    getSelectedZoneAggregator() {
        return (
            this._aggregatorsMap.get(
                this._zoneOptionsService.selectedMapOptions[ZONE_DISPLAY_AGGREGATOR],
            ) || this._aggregators[0]
        );
    }

    /** Returns a colour for a cesium asset entity & time
     * @param imsAsset IMS Asset object
     * @param time Cesium time
     * @returns {Cesium.Color} color
     */
    getAssetColor(imsAsset: Asset, time: Cesium.JulianDate) {
        const selectedAggregator = this.getSelectedAssetAggregator();

        const value = this._valueGetter.getValueForAsset(imsAsset, time, selectedAggregator);

        return this._colorRenderer?.getColor(value) || Cesium.Color.WHITE;
    }

    /** Returns a formatted text for the zone label
     * @param imsAsset IMS Asset object
     * @param time Cesium time
     * @returns asset label text
     */
    getAssetLabelText(imsAsset: Asset | undefined, time: Cesium.JulianDate) {
        const selectedAggregator = this.getSelectedAssetAggregator();
        let labelText: string | undefined;
        if (
            this._assetOptionsService.selectedMapOptions[ASSET_LABEL_DISPLAY_TYPE] ==
            LABEL_TYPE_OPTION_NAME
        ) {
            labelText = imsAsset?.name;
        } else {
            if (imsAsset) {
                labelText = this._labelValueGetter.getFormattedValueForAsset(
                    imsAsset,
                    time,
                    selectedAggregator,
                );
            }
        }
        return labelText ? labelText : '';
    }

    /** Returns a colour for a cesium zone & time
     * @param imsZone ims Zone object
     * @param time Cesium time
     * @returns {Cesium.Color} color
     */
    getZoneColor(imsZone: Zone, time: Cesium.JulianDate) {
        const selectedAggregator = this.getSelectedZoneAggregator();
        const value = this._valueGetter.getValueForZone(imsZone, time, selectedAggregator);
        return this._colorRenderer?.getColor(value) || Cesium.Color.WHITE;
    }

    /** Returns a formatted text for the zone label
     * @param imsZone ims Zone object
     * @param time Cesium time
     * @returns string zone label text
     */
    getZoneLabelText(imsZone: Zone, time: Cesium.JulianDate) {
        let selectedAggregator = this.getSelectedZoneAggregator();
        let labelText = '';
        if (
            this._zoneOptionsService.selectedMapOptions[ZONE_LABEL_DISPLAY_TYPE] ==
            LABEL_TYPE_OPTION_NAME
        ) {
            labelText = imsZone.shortName;
        } else {
            labelText = this._labelValueGetter.getFormattedValueForZone(
                imsZone,
                time,
                selectedAggregator,
            );
        }
        return labelText ? labelText : '';
    }

    /** The master show function for assets. This is used in all other show functions
     * to determine whether or not everything for assets should be hidden
     * @param imsAsset IMS Asset object
     * @param time Cesium time
     * @returns {Boolean}
     */
    getMasterAssetShow(imsAsset: Asset, time: Cesium.JulianDate) {
        return this._imsObjectVisibilityService
            ? this._imsObjectVisibilityService.isAssetShown(imsAsset.id)
            : false;
    }

    /** The master show function for zones. This is used in all other show functions
     * to determine whether or not everything for zones should be hidden
     * @param imsZone IMS Zone
     * @param time Cesium time
     * @returns {Boolean}
     */
    getMasterZoneShow(imsZone: Zone, time: Cesium.JulianDate) {
        return this._imsObjectVisibilityService.isZoneShown(imsZone.id);
    }

    /**
     * Calculates whether or not the label should be shown for the asset for this particular entity visualiser
     * @param imsAsset IMS Asset object
     * @param time Cesium time
     * @returns {boolean} Whether or not the label should be shown for the entity
     * @private
     */
    getAssetLabelShow(imsAsset: Asset, time: Cesium.JulianDate) {
        const mobility =
            imsAsset && imsAsset.type && !isNullOrUndefined(imsAsset.type.mobility)
                ? imsAsset.type.mobility
                : undefined;
        const label =
            this._assetOptionsService.selectedMapOptions[ASSET_LABEL_DISPLAY_VISIBILITY] ||
            SHOW_LABEL_NONE;
        const showAlways = label === SHOW_LABEL_ALWAYS;
        const showByMobility =
            label === SHOW_LABEL_IMMOBILE &&
            (mobility === MOBILITY_IMMOBILE || mobility === MOBILITY_SEMI_MOBILE);
        return (showAlways || showByMobility) && this.getMasterAssetShow(imsAsset, time);
    }

    /**
     * Calculates whether or not the billboard should be shown for the entity
     * @param imsAsset IMS Asset object
     * @param time Cesium time
     * @returns {boolean} Whether or not the billboard should be shown for the entity
     * @private
     */
    getAssetBillboardShow(imsAsset: Asset, time: Cesium.JulianDate) {
        //This is used to determine if we show an asset based on whether or not its show type is none.
        let show = true;
        let assetOption = this._assetOptionsService.selectedMapOptions[ASSET_DISPLAY_COLOUR];
        if (!assetOption || assetOption === SHOW_ASSET_NONE) {
            show = false;
        }

        return !!(show && this.getMasterAssetShow(imsAsset, time));
    }

    /**
     * Calculates whether or not the label should be shown for the zone for this particular entity visualiser
     * @param imsZone IMS Zone
     * @param time Cesium time
     * @returns {boolean} Whether or not the label should be shown for the entity
     * @private
     */
    getZoneLabelShow(imsZone: Zone, time: Cesium.JulianDate) {
        const label =
            this._zoneOptionsService.selectedMapOptions[ZONE_LABEL_DISPLAY_VISIBILITY] ||
            SHOW_LABEL_NONE;
        const showAlways = label === SHOW_LABEL_ALWAYS;
        return showAlways && this.getMasterZoneShow(imsZone, time);
    }

    /**
     * Calculates whether or not the polygon should be shown for the zone for this particular entity visualiser
     * @param imsZone IMS Zone
     * @param time Cesium time
     * @returns {boolean} Whether or not the billboard should be shown for the entity
     * @private
     */
    getZonePolygonShow(imsZone: Zone, time: Cesium.JulianDate) {
        return this.getMasterZoneShow(imsZone, time);
    }

    /**
     * Gets the image path for an entity
     * @param imsAsset IMS Asset object
     * @private
     */
    getAssetImage(imsAsset) {
        if (imsAsset && imsAsset.type) {
            return this._imageUrlService.getUrl('asset_type', imsAsset.type.id, 'png');
        }
        return Constant.Graphics.NULL_BILLBOARD;
    }

    /**
     * @ngdoc property
     * @name aggregators
     * @propertyOf ims.map.service:EntityVisualiser
     * @description
     * Returns the aggregators for this EntityVisualiser instance
     */
    get aggregators() {
        return this._aggregators;
    }

    /**
     * Returns the currently selected aggregator. Should always be truthy
     * @returns {IMSAggregator}
     */
    get selectedAggregator() {
        return (
            this._aggregatorsMap.get(
                this._assetOptionsService.selectedMapOptions[ASSET_DISPLAY_AGGREGATOR],
            ) || this._aggregators[0]
        );
    }

    get labelValueGetter() {
        return this._labelValueGetter;
    }
}
